<template>
    <div class="flex flex-col w-full">
        <!-- Modern Drag & Drop Zone -->
        <div 
            ref="dropZone"
            tabindex="0"
            role="button"
            aria-label="Upload images by clicking, dragging, or pasting"
            @click="openFileInput()"
            @keydown.enter="openFileInput()"
            @keydown.space.prevent="openFileInput()"
            @dragover.prevent="handleDragOver"
            @dragleave.prevent="handleDragLeave"
            @drop.prevent="handleDrop"
            :class="[
                'group relative w-full border-2 border-dashed rounded-2xl p-6 flex flex-col items-center justify-center text-center transition-all duration-200 cursor-pointer outline-none focus:ring-4 focus:ring-indigo-500/20',
                isDragging 
                    ? 'border-indigo-500 bg-indigo-50/80 dark:bg-indigo-950/50 scale-[1.02] shadow-lg ring-4 ring-indigo-500/20' 
                    : 'border-gray-300 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-800/40 hover:border-indigo-500 dark:hover:border-indigo-400 hover:bg-indigo-50/30 dark:hover:bg-indigo-950/20 hover:shadow-sm'
            ]">
            
            <div class="p-3 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 rounded-2xl mb-2.5 group-hover:scale-110 transition-transform duration-200 shadow-xs">
                <CloudArrowUpIcon class="w-6 h-6" />
            </div>

            <h4 class="text-sm font-semibold text-gray-900 dark:text-white mb-0.5">
                {{ isDragging ? ($t('presentation.dropFilesHere') || 'Drop your images here') : ($t('presentation.uploadImagesTitle') || 'Upload Images') }}
            </h4>
            
            <p class="text-xs text-gray-500 dark:text-gray-400 mb-2.5">
                {{ $t('presentation.uploadImagesSubtitle') || 'Click to browse or drag & drop files' }}
            </p>

            <div class="flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400 bg-white/80 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 px-2.5 py-1 rounded-full shadow-xs">
                <span class="font-medium">PNG, JPG, GIF, WebP</span>
                <span>•</span>
                <span class="flex items-center gap-1 font-medium">
                    <ClipboardDocumentIcon class="w-3.5 h-3.5 text-indigo-500" />
                    <span>Ctrl+V</span>
                </span>
            </div>
        </div>
        <input ref="fileInput" type="file" name="file" @change="handleFileInput" accept="image/*" multiple class="hidden">
        
        <!-- Progress Bar -->
        <div v-if="isUploading" class="w-full mt-4 p-3.5 bg-indigo-50/70 dark:bg-indigo-950/40 rounded-xl border border-indigo-100 dark:border-indigo-900/50 shadow-xs">
            <div class="flex justify-between items-center mb-1.5">
                <span class="text-xs font-semibold text-indigo-900 dark:text-indigo-200 flex items-center gap-1.5">
                    <ArrowPathIcon class="w-3.5 h-3.5 animate-spin text-indigo-600 dark:text-indigo-400" />
                    {{ $t('presentation.uploadingImages') || 'Uploading images...' }}
                </span>
                <span class="text-xs font-bold text-indigo-700 dark:text-indigo-300">{{ uploadProgress }}%</span>
            </div>
            <div class="w-full bg-indigo-200/60 dark:bg-indigo-900/60 rounded-full h-2 overflow-hidden">
                <div 
                    class="bg-gradient-to-r from-indigo-500 to-purple-600 h-2 rounded-full transition-all duration-300"
                    :class="{'animate-pulse': uploadProgress < 100 && uploadProgress > 0}"
                    :style="{ width: uploadProgress + '%' }">
                </div>
            </div>
            <p class="text-[11px] text-gray-500 dark:text-gray-400 mt-1.5">
                {{ uploadedCount }} {{ $t('common.of') || 'of' }} {{ totalFilesCount }} {{ totalFilesCount === 1 ? ($t('presentation.file') || 'file') : ($t('presentation.files') || 'files') }}
            </p>
        </div>
        
        <!-- List of Files Component -->
        <div v-if="props.id" class="w-full mt-5">
            <ListFiles ref="listFilesRef" :id="props.id" :direction="props.direction || 'gallery'" @selected="select" />
        </div>
    </div>
</template>
<script setup>
import { ref, defineEmits, defineProps, onMounted, onUnmounted, computed } from "vue";
import { useI18n } from "vue-i18n";
import { CloudArrowUpIcon, ClipboardDocumentIcon, ArrowPathIcon } from '@heroicons/vue/24/solid';
import fileService from "../../service/file.service";
import toastStore from "@/store/toast.store";

const { t } = useI18n();
const emits = defineEmits(['selected'])
const props = defineProps(['id', 'direction'])
const fileInput = ref(null)
const dropZone = ref(null)
const listFilesRef = ref(null)
const uploaded = ref(false);
const error = ref(false);
const isDragging = ref(false);
const isUploading = ref(false);
const uploadedCount = ref(0);
const totalFilesCount = ref(0);

const files = ref([])

// Calculate upload progress percentage
const uploadProgress = computed(() => {
    if (totalFilesCount.value === 0) return 0;
    const actualProgress = Math.round((uploadedCount.value / totalFilesCount.value) * 100);
    // Show at least 10% when uploading to indicate activity (fetch doesn't support progress events)
    if (isUploading.value && actualProgress === 0) {
        return 10;
    }
    return actualProgress;
})

// Accepted image MIME types
const acceptedImageTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp']

// Check if file is an image
const isImageFile = (file) => {
    if (file.type && acceptedImageTypes.includes(file.type.toLowerCase())) {
        return true
    }
    // Fallback to filename check if MIME type is not available
    return /\.(png|jpe?g|gif|webp)$/i.test(file.name)
}

// Process files from any source (input, drag, paste)
const processFiles = (fileList) => {
    const imageFiles = Array.from(fileList).filter(isImageFile)
    
    if (imageFiles.length === 0) {
        toastStore.dispatch('addMessage', {
            message: t('presentation.onlyImagesAllowed') || 'Please upload only image files (PNG, JPEG, JPG, GIF, WEBP)',
            icon: 'warning',
            ttl: 4000
        });
        return
    }

    // Reset upload state
    isUploading.value = true;
    uploadedCount.value = 0;
    totalFilesCount.value = imageFiles.length;
    error.value = false;
    uploaded.value = false;

    // Process each image file
    imageFiles.forEach(file => {
        // Add metadata immediately (before async operations)
        // Store original name for display (can't mutate file.name as it's read-only)
        file.originalName = file.name
        // Store hashed name for submission
        file.hashedName = hashFileName(file.name)
        // Track upload status per file
        file.uploadStatus = 'pending'; // pending, uploading, success, error
        
        const reader = new FileReader();
        
        reader.onload = () => {
            const url = URL.createObjectURL(file);
            // Store URL for preview
            file.url = url
            
            files.value.push(file);
        };
        reader.readAsDataURL(file);
    })

    // Submit files to server (hashedName is now available)
    submitFile(props.id, imageFiles)
}

// Handle file input change
const handleFileInput = (event) => {
    if (event.target.files && event.target.files.length > 0) {
        processFiles(event.target.files)
        // Reset input to allow selecting the same file again
        event.target.value = ''
    }
}

// Handle drag over
const handleDragOver = (event) => {
    event.preventDefault()
    isDragging.value = true
}

// Handle drag leave
const handleDragLeave = (event) => {
    event.preventDefault()
    // Only set isDragging to false if we're leaving the drop zone
    if (!dropZone.value?.contains(event.relatedTarget)) {
        isDragging.value = false
    }
}

// Handle drop
const handleDrop = (event) => {
    event.preventDefault()
    isDragging.value = false
    
    const droppedFiles = event.dataTransfer.files
    if (droppedFiles && droppedFiles.length > 0) {
        processFiles(droppedFiles)
    }
}

// Handle paste from clipboard
const handlePaste = (event) => {
    const items = event.clipboardData?.items
    
    if (!items || items.length === 0) return
    
    const imageFiles = []
    
    for (let i = 0; i < items.length; i++) {
        const item = items[i]
        
        // Check if the item is an image
        if (item.type && item.type.indexOf('image') !== -1) {
            const blob = item.getAsFile()
            if (blob) {
                // Determine file extension from MIME type
                const extension = blob.type.split('/')[1] || 'png'
                // Create a File object with a name including timestamp for uniqueness
                const timestamp = Date.now()
                const randomId = Math.random().toString(36).substring(2, 9)
                const file = new File([blob], `pasted-image-${timestamp}-${randomId}.${extension}`, { type: blob.type })
                imageFiles.push(file)
            }
        }
    }
    
    if (imageFiles.length > 0) {
        event.preventDefault() // Prevent default paste behavior
        processFiles(imageFiles)
    }
}

const openFileInput = () => {
    fileInput.value.click();
}
const submitFile = (id, filesToUpload) => {
    const formData = new FormData();
    formData.append('id', id);
    
    // Mark all files as uploading
    filesToUpload.forEach(f => {
        if (f.uploadStatus === 'pending') {
            f.uploadStatus = 'uploading';
        }
    });
    
    Array.from(filesToUpload).forEach( f => {
        // Use hashedName if available, otherwise hash the current name
        const hashedName = f.hashedName || hashFileName(f.name || f.originalName)
        // Create a new file with hashed name (File objects can't be mutated)
        const renamedFile = new File([f], hashedName, { type: f.type })
        formData.append('files', renamedFile);
    })
    
    fileService.submitFiles(formData)
    .then(async () => {
        // Mark all files as successfully uploaded
        filesToUpload.forEach(f => {
            f.uploadStatus = 'success';
        });
        uploadedCount.value = totalFilesCount.value;
        setUploaded();
        
        // Reload the list of files after a short delay to ensure server has processed
        setTimeout(() => {
            if (listFilesRef.value && listFilesRef.value.reload) {
                listFilesRef.value.reload();
            }
        }, 500);
    })
    .catch(err => {
        console.error(err);
        // Mark all files as error
        filesToUpload.forEach(f => {
            f.uploadStatus = 'error';
        });
        setError();
    })
    .finally(() => {
        isUploading.value = false;
    })
}

// Helper function to hash filename and remove spaces
const hashFileName = (filename) => {
    // Remove spaces and special characters, keep extension
    const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'))
    const extension = filename.substring(filename.lastIndexOf('.'))
    
    // Create a simple hash from the original name
    let hash = 0
    for (let i = 0; i < nameWithoutExt.length; i++) {
        const char = nameWithoutExt.charCodeAt(i)
        hash = ((hash << 5) - hash) + char
        hash = hash & hash // Convert to 32-bit integer
    }
    
    // Convert to positive hex string and remove spaces
    const hashedName = Math.abs(hash).toString(16)
    return `${hashedName}${extension}`
}

const select = (f) => {
    emits('selected', f)
}
const setUploaded = () => {
    uploaded.value = true;
    error.value = false;
}

const setError = () => {
    uploaded.value = false;
    error.value = true;
    isUploading.value = false;
}

// const removeImage = (file) => {
//     if (confirm('Are you sure you want to delete the file? It will delete on server.')) {
//         files.value = files.value.filter( f => f.url != file.url);
//         // Use hashedName for deletion, fallback to name
//         const fileName = file.hashedName || file.name
//         const request = {
//             url: props.id+"/"+fileName 
//         }
//         fileService.removeImage(request).then(async (response) => {
//             await response.json();
//             // Reload the list of files after deletion
//             if (listFilesRef.value && listFilesRef.value.reload) {
//                 listFilesRef.value.reload();
//             }
//         })
//         .catch(error => {
//             console.error(error);
//         })
//     }
// }

// const removeAllFiles = () => {
//     if (confirm('This action will delete all files from server. Are you sure you want continue?')){
//         let urls = files.value.map( f=> props.id+"/"+(f.hashedName || f.name) );
//         files.value=[];
//         fileService.removeAllFiles(urls).then(async (response) => {
//             await response.json();
//             // Reload the list of files after deletion
//             if (listFilesRef.value && listFilesRef.value.reload) {
//                 listFilesRef.value.reload();
//             }
//         })
//         .catch(error => {
//             console.error(error);
//         })
//     }
// }

// Attach paste event listener when component mounts
onMounted(() => {
    // Add paste event listener to the window to catch paste events globally
    // This allows users to paste images from clipboard anywhere while the component is mounted
    window.addEventListener('paste', handlePaste, true) // Use capture phase for better control
})

// Clean up event listeners when component unmounts
onUnmounted(() => {
    window.removeEventListener('paste', handlePaste, true)
    // Clean up object URLs to prevent memory leaks
    files.value.forEach(file => {
        if (file.url && file.url.startsWith('blob:')) {
            URL.revokeObjectURL(file.url)
        }
    })
})

</script>
